import React from 'react';
import {
  AimOutlined,
  DeleteOutlined,
  EnvironmentOutlined,
  InfoCircleOutlined,
  PlusOutlined,
} from '@ant-design/icons';
import { Button, Form, Input, Tooltip, theme } from 'antd';
import { useTranslation } from 'react-i18next';
import { buildTransportAddressAnchorId } from './transportRequiredFields';

type AddressFieldName = 'loadingAddresses' | 'deliveryAddresses';
type AddressFieldVariant = 'loading' | 'delivery';

interface TransportAddressListFieldProps {
  name: AddressFieldName;
  label: string;
  placeholder: string;
  errorMessage: string;
  variant: AddressFieldVariant;
}

const VARIANT_META: Record<
  AddressFieldVariant,
  {
    icon: React.ReactNode;
    // Short muted one-liner under the title.
    helperShortKey:
      | 'transport:helpers.loadingAddressesShort'
      | 'transport:helpers.deliveryAddressesShort';
    // Full original instruction, now surfaced only inside the Tooltip.
    helperKey:
      | 'transport:helpers.loadingAddresses'
      | 'transport:helpers.deliveryAddresses';
    stopKey: 'transport:labels.loadingStop' | 'transport:labels.deliveryStop';
  }
> = {
  loading: {
    icon: <EnvironmentOutlined />,
    helperShortKey: 'transport:helpers.loadingAddressesShort',
    helperKey: 'transport:helpers.loadingAddresses',
    stopKey: 'transport:labels.loadingStop',
  },
  delivery: {
    icon: <AimOutlined />,
    helperShortKey: 'transport:helpers.deliveryAddressesShort',
    helperKey: 'transport:helpers.deliveryAddresses',
    stopKey: 'transport:labels.deliveryStop',
  },
};

const TransportAddressListField: React.FC<TransportAddressListFieldProps> = ({
  name,
  label,
  placeholder,
  errorMessage,
  variant,
}) => {
  const { t } = useTranslation(['transport', 'common']);
  const { token } = theme.useToken();
  const form = Form.useFormInstance();
  const variantMeta = VARIANT_META[variant];
  const validateAddressList = () => {
    window.setTimeout(() => {
      void form.validateFields([name]).catch(() => undefined);
    }, 0);
  };

  return (
    <Form.List
      name={name}
      rules={[
        {
          validator: async (_, value) => {
            const filledValues = Array.isArray(value)
              ? value.map((entry) => String(entry ?? '').trim()).filter(Boolean)
              : [];

            if (filledValues.length === 0) {
              throw new Error(errorMessage);
            }
          },
        },
      ]}
    >
      {(fields, { add, remove }, { errors }) => (
        <section
          id={buildTransportAddressAnchorId(variant)}
          className={[
            'transport-address-list',
            `transport-address-list--${variant}`,
            errors.length > 0 ? 'transport-address-list--error' : '',
          ].filter(Boolean).join(' ')}
        >
          {/* Column header: icon + title on the left, one short muted line
              with an info tooltip carrying the full original instruction. */}
          <div
            className="transport-address-list__header"
            style={{ display: 'flex', alignItems: 'flex-start', gap: 8, marginBottom: 12 }}
          >
            <span
              className="transport-address-list__icon"
              style={{ color: token.colorPrimary, display: 'inline-flex', marginTop: 2 }}
            >
              {variantMeta.icon}
            </span>
            <div style={{ minWidth: 0 }}>
              <div
                className="transport-address-list__title"
                style={{
                  color: errors.length > 0 ? token.colorError : token.colorText,
                  fontWeight: 500,
                }}
              >
                <span
                  aria-hidden="true"
                  className="transport-address-list__required-marker"
                  style={{ color: token.colorError, marginInlineEnd: 4 }}
                >
                  *
                </span>
                {label}
              </div>
              <div
                className="transport-address-list__helper"
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 6,
                  color: token.colorTextTertiary,
                  fontSize: token.fontSizeSM,
                }}
              >
                <span>{t(variantMeta.helperShortKey)}</span>
                <Tooltip title={t(variantMeta.helperKey)}>
                  <InfoCircleOutlined
                    style={{ color: token.colorTextTertiary, cursor: 'help' }}
                  />
                </Tooltip>
              </div>
            </div>
          </div>

          <div
            className="transport-address-list__items"
            style={{ display: 'flex', flexDirection: 'column', gap: 8 }}
          >
            {fields.map((field, index) => {
              const { key, ...fieldProps } = field;
              const fieldValue = form.getFieldValue([name, field.name]);
              const fieldHasError = errors.length > 0
                && String(fieldValue ?? '').trim().length === 0;

              return (
                <div
                  key={key}
                  className="transport-address-list__item"
                  style={{
                    border: `1px solid ${fieldHasError
                      ? token.colorErrorBorder
                      : token.colorBorderSecondary}`,
                    background: fieldHasError ? token.colorErrorBg : token.colorFillQuaternary,
                    borderRadius: token.borderRadiusLG,
                    padding: '8px 10px',
                  }}
                >
                  <div
                    className="transport-address-list__item-head"
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      marginBottom: 6,
                    }}
                  >
                    <span
                      className="transport-address-list__item-label"
                      style={{
                        color: token.colorTextSecondary,
                        fontSize: token.fontSizeSM,
                        fontWeight: 500,
                      }}
                    >
                      {t('transport:labels.pointLabel', { index: index + 1 })}
                    </span>
                    {fields.length > 1 ? (
                      <Tooltip title={t('transport:actions.removeLocation')}>
                        <Button
                          type="text"
                          danger
                          size="small"
                          aria-label={t('transport:actions.removeLocation')}
                          icon={<DeleteOutlined />}
                          onClick={() => remove(field.name)}
                        />
                      </Tooltip>
                    ) : null}
                  </div>

                  <Form.Item
                    {...fieldProps}
                    name={field.name}
                    validateTrigger={['onChange', 'onBlur']}
                    style={{ margin: 0 }}
                  >
                    <Input.TextArea
                      autoSize={{ minRows: 2, maxRows: 4 }}
                      className="transport-address-list__textarea"
                      status={fieldHasError ? 'error' : undefined}
                      aria-invalid={fieldHasError}
                      aria-describedby={fieldHasError
                        ? `${buildTransportAddressAnchorId(variant)}-error_help`
                        : undefined}
                      onBlur={validateAddressList}
                      onChange={validateAddressList}
                      placeholder={placeholder}
                    />
                  </Form.Item>
                </div>
              );
            })}
          </div>

          {/* Classic antd "add item" pattern: dashed, full-column-width
              button below the list. */}
          <Button
            className="transport-address-list__add"
            type="dashed"
            block
            icon={<PlusOutlined />}
            onClick={() => add('')}
            style={{ marginTop: 8 }}
          >
            {t('transport:actions.addLocation')}
          </Button>

          {errors.length > 0 ? (
            <div style={{ marginTop: 4 }}>
              <Form.ErrorList
                fieldId={`${buildTransportAddressAnchorId(variant)}-error`}
                errors={errors}
                className="transport-address-list__error"
              />
            </div>
          ) : null}
        </section>
      )}
    </Form.List>
  );
};

export default TransportAddressListField;
